import React, { useState, useEffect, useMemo } from 'react';
import { useWishlistContext } from '../context/WishlistContext';
import { useNavigate } from 'react-router-dom';
import {
  SlidersHorizontal,
  Search,
  Heart,
  Eye,
  Star,
  Plus,
  Layers,
  Sparkles
} from 'lucide-react';
import { formatVND } from '../utils/formatters';
import { categoryService } from './services/categoryService';

export default function ProductSection({
  products = [],
  addToCart,
  selectedCategory = 'all',
  setSelectedCategory = () => {},
  selectedCategoryLabel = 'Tất Cả Sản Phẩm',
  setSelectedCategoryLabel = () => {},
  onQuickView
}) {
  const { wishlist: globalWishlist, toggleWishlist: globalToggleWishlist } = useWishlistContext();

  const [priceRange, setPriceRange] = useState(100000000);
  const [sortBy, setSortBy] = useState('default');
  const [searchQuery, setSearchQuery] = useState('');
  const [visibleCount, setVisibleCount] = useState(8);
  const [categories, setCategories] = useState([]);
  
  const [subCategories, setSubCategories] = useState([]);
  const [selectedSubCategory, setSelectedSubCategory] = useState('all');
  const navigate = useNavigate();
  // Load danh mục từ Backend
  useEffect(() => {
    categoryService.getAllCategories()
      .then((res) => {
        const list = Array.isArray(res) ? res : (res.data || []);
        setCategories(list);
      })
      .catch((err) => console.error("Lỗi tải danh mục:", err));
  }, []);

  // Xử lý danh sách subCategories phân cấp khi selectedCategory hoặc categories thay đổi
  useEffect(() => {
    setVisibleCount(8);
    setSelectedSubCategory('all');

    if (
      selectedCategory === 'all' || 
      selectedCategory === 'Tất Cả Sản Phẩm' || 
      !Array.isArray(categories) || 
      categories.length === 0
    ) {
      setSubCategories([]);
      return;
    }

    const target = String(selectedCategory).toLowerCase().trim();
    const targetLabel = String(selectedCategoryLabel).toLowerCase().trim();

    const matchedCategory = categories.find(c => {
      const cId = String(c.id ?? '').trim().toLowerCase();
      const cKey = String(c.key ?? '').trim().toLowerCase();
      const cName = String(c.name ?? '').trim().toLowerCase();
      return cId === target || cKey === target || cName === target || cName === targetLabel;
    });

    if (matchedCategory) {
      const getNestedHierarchy = (cat, depth = 1) => {
        let results = [];
        const children = cat.subCategories || cat.children || cat.sub_categories || [];
        const directChildren = categories.filter(sub => {
          const subParentId = String(sub.parentId ?? sub.parent_id ?? sub.parent?.id ?? '').trim();
          return subParentId === String(cat.id).trim();
        });

        const combined = [...children, ...directChildren];
        const uniqueMap = new Map();
        combined.forEach(item => {
          if (item && item.id) uniqueMap.set(String(item.id), item);
        });

        uniqueMap.forEach((subCat) => {
          results.push({ ...subCat, depth });
          results = results.concat(getNestedHierarchy(subCat, depth + 1));
        });

        return results;
      };

      const subs = getNestedHierarchy(matchedCategory);
      setSubCategories(subs);
    } else {
      setSubCategories([]);
    }
  }, [selectedCategory, selectedCategoryLabel, categories]);

  useEffect(() => {
    setVisibleCount(8);
  }, [selectedSubCategory, priceRange, sortBy, searchQuery]);

  const openQuickView = (product) => {
    if (onQuickView) {
      onQuickView(product);
    }
  };
// Hàm chuyển hướng sang trang chi tiết
  const handleProductClick = (product) => {
    // Ưu tiên dùng realId (ID thật trong DB), nếu không có thì dùng id hiển thị
    const targetId = product.realId || product.id;
    navigate(`/product/${targetId}`);
  };
  // Logic lọc sản phẩm phân cấp
  const filteredProducts = useMemo(() => {
    if (!Array.isArray(products)) return [];

    return products
      .filter((product) => {
        let matchCategory = true;

        if (selectedCategory === 'all' || selectedCategory === 'Tất Cả Sản Phẩm') {
          matchCategory = true;
        } else {
          const target = String(selectedCategory).toLowerCase().trim();
          const targetLabel = String(selectedCategoryLabel).toLowerCase().trim();
          
          // Lấy chính xác ID danh mục của sản phẩm
          const pCatId = String(
            product.categoryId ?? 
            product.category_id ?? 
            product.category?.id ?? 
            product.catId ?? 
            product.category?.categoryId ?? 
            ''
          ).trim();

          const pCatName = String(
            product.categoryName || 
            product.category?.name || 
            product.category_name || 
            (typeof product.category === 'string' ? product.category : '') || 
            ''
          ).toLowerCase().trim();

          // Tìm danh mục được chọn từ mảng categories
          const matchedCategory = Array.isArray(categories) && categories.length > 0
            ? categories.find(c => {
                const cId = String(c.id ?? '').trim().toLowerCase();
                const cKey = String(c.key ?? '').trim().toLowerCase();
                const cName = String(c.name ?? '').trim().toLowerCase();
                return cId === target || cKey === target || cName === target || cName === targetLabel;
              })
            : null;

          if (matchedCategory) {
            // Hàm lấy toàn bộ ID con cháu đệ quy
            const getAllDescendantIds = (cat) => {
              let ids = [String(cat.id).trim()];
              const directChildren = categories.filter(sub => {
                const sPid = String(sub.parentId ?? sub.parent_id ?? sub.parent?.id ?? '').trim();
                return sPid === String(cat.id).trim();
              });
              directChildren.forEach(child => {
                ids = ids.concat(getAllDescendantIds(child));
              });
              return ids;
            };

            if (selectedSubCategory !== 'all') {
              const subTarget = String(selectedSubCategory).trim();
              const subCatObj = categories.find(c => 
                String(c.id).trim() === subTarget || 
                String(c.name || '').toLowerCase().trim() === subTarget.toLowerCase()
              );
              
              const validSubIds = subCatObj ? getAllDescendantIds(subCatObj) : [subTarget];
              matchCategory = validSubIds.includes(pCatId);
            } else {
              // Trường hợp chọn Danh mục Cha (Tất cả nhánh con/cháu)
              const validIds = [...new Set(getAllDescendantIds(matchedCategory).filter(Boolean))];
              matchCategory = validIds.includes(pCatId);
            }
          } else {
            // Dự phòng khi danh mục chưa load kịp
            matchCategory = pCatId === target || (pCatName && (pCatName === targetLabel || pCatName.includes(targetLabel)));
          }
        }

        // Lọc theo Giá
        const rawPrice = product.price ?? 0;
        const productPrice = typeof rawPrice === 'string' 
          ? Number(rawPrice.replace(/[^0-9]/g, '')) || 0 
          : Number(rawPrice) || 0;

        const matchPrice = productPrice <= priceRange;

        // Lọc theo Từ khóa Tìm kiếm
        const matchSearch = searchQuery 
          ? String(product.name || '').toLowerCase().includes(searchQuery.toLowerCase()) 
          : true;

        return matchCategory && matchPrice && matchSearch;
      })
      .sort((a, b) => {
        const stockA = Number(a?.stock ?? a?.quantity ?? a?.countInStock ?? 1);
        const stockB = Number(b?.stock ?? b?.quantity ?? b?.countInStock ?? 1);
        const isOutOfStockA = stockA <= 0 || a.status === 'OUT_OF_STOCK';
        const isOutOfStockB = stockB <= 0 || b.status === 'OUT_OF_STOCK';

        if (isOutOfStockA !== isOutOfStockB) {
          return isOutOfStockA ? 1 : -1;
        }

        const priceA = typeof a.price === 'string' ? Number(a.price.replace(/[^0-9]/g, '')) || 0 : Number(a.price ?? 0);
        const priceB = typeof b.price === 'string' ? Number(b.price.replace(/[^0-9]/g, '')) || 0 : Number(b.price ?? 0);

        if (sortBy === 'newest' || sortBy === 'default') {
          const idA = Number(a.id || a.realId || 0);
          const idB = Number(b.id || b.realId || 0);
          return idB - idA;
        }
        
        if (sortBy === 'price-asc') return priceA - priceB;
        if (sortBy === 'price-desc') return priceB - priceA;
        if (sortBy === 'rating') return (b.star ?? 5) - (a.star ?? 5);
        return 0;
      });
  }, [products, selectedCategory, selectedCategoryLabel, selectedSubCategory, categories, priceRange, sortBy, searchQuery]);

  const displayedProducts = useMemo(() => {
    return filteredProducts.slice(0, visibleCount);
  }, [filteredProducts, visibleCount]);

  return (
      <section id="products-section" className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Header Section */}
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-6 gap-4 border-b border-slate-100 pb-6">
            <div>
              <span className="text-xs font-bold uppercase tracking-widest text-indigo-600 flex items-center gap-1">
                <Sparkles className="w-3.5 h-3.5" /> {selectedCategory !== 'all' && selectedCategory !== 'Tất Cả Sản Phẩm' ? 'DANH MỤC ĐANG XEM' : 'SẢN PHẨM NỔI BẬT'}
              </span>
              <h2 className="text-3xl font-extrabold text-slate-900 font-serif mt-1">
                {selectedCategoryLabel}
              </h2>
            </div>

            {/* Bộ lọc khoảng giá & Sắp xếp */}
            <div className="flex flex-wrap items-center gap-3">
              {/* Box Khoảng giá */}
              <div className="flex items-center space-x-3 bg-slate-50 border border-slate-200/80 px-4 py-2 rounded-2xl shadow-xs">
                <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Lọc giá:</span>
                <span className="text-xs font-extrabold text-indigo-600">{formatVND(priceRange)}</span>
                <input
                    type="range"
                    min="0"
                    max="50000000"
                    step="500000"
                    value={priceRange}
                    onChange={(e) => setPriceRange(Number(e.target.value))}
                    className="w-28 sm:w-36 accent-indigo-600 cursor-pointer"
                />
              </div>

              {/* Box Sắp xếp */}
              <div className="flex items-center space-x-2 bg-slate-50 border border-slate-200/80 px-4 py-2 rounded-2xl text-xs font-semibold text-slate-700 shadow-xs">
                <SlidersHorizontal className="w-3.5 h-3.5 text-indigo-600" />
                <span>Sắp xếp:</span>
                <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="bg-transparent font-bold text-slate-900 focus:outline-none cursor-pointer"
                >
                  <option value="newest">Mới nhất</option>
                  <option value="default">Mặc định</option>
                  <option value="price-asc">Giá: Thấp đến Cao</option>
                  <option value="price-desc">Giá: Cao đến Thấp</option>
                  <option value="rating">Đánh giá cao nhất</option>
                </select>
              </div>
            </div>
          </div>

          {/* Thanh lọc phụ cho Danh mục con / cháu */}
          {subCategories.length > 0 && (
            <div className="mb-8 p-5 bg-gradient-to-r from-slate-50 via-indigo-50/20 to-slate-50 rounded-3xl border border-slate-200/70 shadow-sm flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-slate-600 uppercase tracking-wider flex items-center gap-2">
                  <Layers className="w-4 h-4 text-indigo-600" /> Danh mục phân nhánh của: <span className="text-indigo-600 font-extrabold">{selectedCategoryLabel}</span>
                </span>
                {selectedSubCategory !== 'all' && (
                  <button 
                    onClick={() => setSelectedSubCategory('all')} 
                    className="text-xs font-bold text-indigo-600 hover:underline cursor-pointer"
                  >
                    Xem tất cả nhánh
                  </button>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-2 pt-1">
                <button
                  onClick={() => setSelectedSubCategory('all')}
                  className={`px-4 py-2 rounded-2xl text-xs font-bold transition shadow-xs cursor-pointer ${
                    selectedSubCategory === 'all'
                      ? 'bg-indigo-600 text-white shadow-md shadow-indigo-600/20'
                      : 'bg-white text-slate-700 hover:bg-slate-100 border border-slate-200'
                  }`}
                >
                  Tất cả ({selectedCategoryLabel})
                </button>

                {subCategories.map((sub) => {
                  const isSelected = String(selectedSubCategory) === String(sub.id) || String(selectedSubCategory) === String(sub.name);
                  const indentClass = sub.depth > 1 ? 'ml-2 border-l-2 border-indigo-200 pl-2' : '';

                  return (
                    <button
                      key={sub.id}
                      onClick={() => setSelectedSubCategory(sub.id)}
                      className={`px-4 py-2 rounded-2xl text-xs font-bold transition shadow-xs flex items-center gap-1.5 cursor-pointer ${indentClass} ${
                        isSelected
                          ? 'bg-indigo-600 text-white shadow-md shadow-indigo-600/20'
                          : 'bg-white text-slate-700 hover:bg-indigo-50 border border-slate-200/80'
                      }`}
                    >
                      <span className="text-[10px] opacity-70">↳</span>
                      <span>{sub.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Grid Sản Phẩm */}
          <div>
            {filteredProducts.length === 0 ? (
                <div className="text-center py-16 bg-slate-50 rounded-3xl border border-dashed border-slate-200">
                  <Search className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <h3 className="font-bold text-slate-800 text-lg">Không tìm thấy sản phẩm phù hợp</h3>
                  <p className="text-xs text-slate-400 mt-1">Vui lòng thử lại với từ khóa hoặc bộ lọc giá khác.</p>
                  <button
                      onClick={() => { setSelectedCategory('all'); setSelectedCategoryLabel('Tất Cả Sản Phẩm'); setSelectedSubCategory('all'); setSearchQuery(''); setPriceRange(15000000); }}
                      className="mt-4 bg-indigo-600 text-white text-xs font-bold px-5 py-2.5 rounded-full hover:bg-indigo-700 transition cursor-pointer"
                  >
                    Xóa Bộ Lọc
                  </button>
                </div>
            ) : (
              <>
                <div
                 className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                  {displayedProducts.map((product) => {
                    const isWish = globalWishlist.includes(product.id || product.realId);
                    const imgUrl = product.thumbnail || product.images?.[0] || 'https://placeholder.com';

                    return (
                        <div
                            key={product.id || product.realId}
                            onClick={() => handleProductClick(product)}
                            className="group bg-white border border-slate-100 rounded-3xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col justify-between"
                        >
                          {/* Image */}
                          <div className="relative bg-slate-100 aspect-square overflow-hidden flex items-center justify-center cursor-pointer"
                               onClick={() => openQuickView(product)}
                          >
                            <img
                                src={imgUrl}
                                alt={product.name}
                                className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
                            />

                            {product.stock === 0 || product.status === 'OUT_OF_STOCK' ? (
                                <span className="absolute top-3 left-3 bg-rose-600 text-white text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider">
                          HẾT HÀNG
                        </span>
                            ) : (
                                <span className="absolute top-3 left-3 bg-emerald-600 text-white text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider">
                          CÒN HÀNG
                        </span>
                            )}

                            <div className="absolute top-3 right-3 flex flex-col space-y-2 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    globalToggleWishlist(product.id || product.realId);
                                  }}
                                  className={`p-2 bg-white/90 hover:bg-white text-slate-700 rounded-full shadow-md backdrop-blur-md transition cursor-pointer ${
                                      isWish ? 'text-rose-500' : 'hover:text-rose-500'
                                  }`}
                              >
                                <Heart className={`w-4 h-4 ${isWish ? 'fill-rose-500' : ''}`} />
                              </button>

                              <button
                                  onClick={() => openQuickView(product)}
                                  className="p-2 bg-white/90 hover:bg-white text-slate-700 hover:text-indigo-600 rounded-full shadow-md backdrop-blur-md transition cursor-pointer"
                                  title="Xem nhanh chi tiết"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                            </div>
                          </div>

                          {/* Content */}
                          <div className="p-5 flex-1 flex flex-col justify-between space-y-3">
                            <div>
                              <div className="flex items-center space-x-1 text-amber-400 text-xs mb-1">
                                <Star className="w-3.5 h-3.5 fill-amber-400" />
                                <span className="font-bold text-slate-800">{product.star || 5}</span>
                              </div>
                              <h3
                                  onClick={() => openQuickView(product)}
                                  className="font-bold text-slate-900 text-sm group-hover:text-indigo-600 transition line-clamp-2 cursor-pointer hover:underline"
                              >
                                {product.name}
                              </h3>
                            </div>

                            <div className="flex items-center justify-between pt-2 border-t border-slate-50">
                              <div>
                                <div className="text-base font-extrabold text-slate-900">{formatVND(product.price ?? 0)}</div>
                              </div>
                              {(() => {
                                const stockVal = Number(product?.stock ?? product?.quantity ?? product?.countInStock ?? 1);
                                const isOutOfStock = stockVal <= 0 || product.status === 'OUT_OF_STOCK';

                                return (
                                    <button
                                        type="button"
                                        disabled={isOutOfStock}
                                        onClick={() => !isOutOfStock && addToCart && addToCart(product)}
                                        className={`p-2.5 rounded-2xl transition shadow-md flex items-center justify-center ${
                                            isOutOfStock
                                                ? 'bg-slate-200 text-slate-400 cursor-not-allowed shadow-none'
                                                : 'bg-slate-900 hover:bg-indigo-600 text-white cursor-pointer shadow-md'
                                        }`}
                                        title={isOutOfStock ? 'Sản phẩm đã hết hàng' : 'Thêm vào giỏ hàng'}
                                    >
                                      <Plus className="w-4 h-4" />
                                    </button>
                                );
                              })()}
                            </div>
                          </div>

                        </div>
                    );
                  })}
                </div>

                {/* Nút Xem Thêm */}
                {visibleCount < filteredProducts.length && (
                  <div className="text-center mt-12">
                    <button
                      onClick={() => setVisibleCount((prev) => prev + 8)}
                      className="bg-slate-900 hover:bg-indigo-600 text-white font-bold text-xs px-8 py-4 rounded-2xl transition shadow-md hover:shadow-lg cursor-pointer"
                    >
                      Xem thêm ({filteredProducts.length - visibleCount} sản phẩm khác)
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

        </div>
      </section>
  );
}